import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import jsCookie from 'js-cookie';
import { Levels } from '../../components/layout/Toast';
import ErrorStore from '../../zustand/error';
import getAuthToken from '../../services/getAuthToken';
import toast from 'react-hot-toast';

// Platform-aware baseURL.
//
// Web (browser bundle) hits the API via api.scconnect.nl / api-staging.scconnect.nl
// — sibling subdomain of the SPA's *.scconnect.nl host. Same registrable
// domain = same-site, which lets the HttpOnly auth cookie flow on cross-
// subdomain XHRs (Week 8 subdomain consolidation; closes XSS-token-exfil
// risk that the localStorage path was exposed to).
//
// Mobile (Capacitor) hits the App Service default *.azurewebsites.net
// hostnames directly. It's not subject to browser cookie rules — auth
// rides in the x-auth-token header from Capacitor Preferences storage.
// The server's shouldSetAuthCookie() returns false for these direct hits
// so we don't pin a stray host-only cookie that no client path can read.
const isNative = Capacitor.isNativePlatform();
const env = process.env.REACT_APP_ENV;

let baseURL;
if (env === 'development') {
  baseURL = 'http://localhost:5000/api';
} else if (env === 'staging') {
  baseURL = isNative
    ? 'https://scconnect-api-staging-bfdachf2cfcphzet.westeurope-01.azurewebsites.net/api'
    : 'https://api-staging.scconnect.nl/api';
} else {
  baseURL = isNative
    ? 'https://scconnect-api.azurewebsites.net/api'
    : 'https://api.scconnect.nl/api';
}

// Endpoints where a 401 means "the user got a credential wrong", NOT "the
// session is gone". These pages render the failure inline, so bouncing to
// /login would destroy the context the user is working in.
//
//   /mfa/verify + request-email-code — 401 for both bad-code and expired
//     challenge; the second-step page distinguishes them itself.
//   /mfa/disable — 401 for a wrong password (mfa.service.ts:115). Without
//     this entry, fat-fingering the password while turning two-step login
//     off throws the user out to /login instead of showing the error.
//
// Deliberately NOT listed: the cohort-enrolment endpoints. Their 401 really
// does mean the enrolment session expired, so the bounce is correct there.
const AUTH_ERROR_HANDLED_LOCALLY = [
  '/mfa/verify',
  '/mfa/verify/request-email-code',
  '/mfa/disable',
];

const handlesAuthErrorLocally = (requestUrl = '') =>
  AUTH_ERROR_HANDLED_LOCALLY.some((path) => requestUrl.includes(path));

// withCredentials lets the auth cookie travel cross-subdomain on web.
// Harmless on mobile (Capacitor doesn't use browser cookies for these
// requests) — keep set unconditionally rather than adding a branch.
export const service = axios.create({
  baseURL,
  withCredentials: true,
});

// On mobile, fetch the bearer token from Capacitor Preferences and attach
// it as x-auth-token. On web, getAuthToken() returns null — the cookie
// rides with withCredentials, no header needed. The `if (token)` guard
// makes the web path a no-op without an extra branch here.
service.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

service.interceptors.response.use(
  (response) => {
    const statusCode = response.status;
    const message = response.data?.message || 'Voltooid';

    if (statusCode === 201) {
      toast.success(message, {
        position: 'bottom-center',
        icon: '✅',
      });
    }

    return response;
  },
  (error) => {
    // error.response is undefined for network errors, aborts, and CORS
    // rejections — accessing .status used to crash the interceptor, which
    // re-threw a TypeError instead of the original error and made every
    // such failure look silent at the call site. Guard the access so the
    // original error propagates cleanly.
    const statusCode = error?.response?.status;

    // Some endpoints return 401 for a wrong credential rather than a dead
    // session; those pages show the error inline. See the list above.
    const requestUrl = error?.config?.url || '';

    if (statusCode === 401 && !handlesAuthErrorLocally(requestUrl)) {
      jsCookie.remove('Authorization');
      if (!window.location.href.includes('/login')) {
        window.location.href = '/login';
      }
    }

    // Show errors. statusCode being undefined (network/abort/CORS) still
    // deserves a surfaced toast so the user isn't left wondering.
    if (statusCode === undefined || statusCode > 401) {
      ErrorStore.setState((state) => {
        const newErrors = [
          ...state.errors,
          {
            level: Levels.error,
            message: JSON.stringify(
              error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data ||
                error?.message ||
                'Netwerkfout — kon de server niet bereiken.'
            ),
          },
        ];
        return {
          ...state,
          errors: newErrors,
        };
      });
    }

    return Promise.reject(error);
  }
);

// export const configHeaders = async () => {
//   const token = await getAuthToken();
// };
